/**
 * Drafted rationales (B5).
 *
 * A worker may draft the words a reviewer would write. It may never become the
 * reviewer. The draft is evidence; the signature is the decision — so
 * `authority: "draft"` is a literal that cannot widen to `"decision"`, and the
 * only path from one to the other requires a human actor and final text.
 *
 * Drafting also creates a rubber-stamping risk that is invisible per-decision:
 * a reviewer who signs every draft unedited looks identical, one row at a time,
 * to one who read carefully. `draftedRationaleEditRate` makes that visible in
 * aggregate, which is the only level at which it can be seen.
 */
function hasText(value) {
    return typeof value === "string" && value.trim().length > 0;
}
/** Levenshtein distance — the edit measure the scorecard reports. */
export function editDistance(left, right) {
    if (left === right)
        return 0;
    const rows = left.length + 1;
    const cols = right.length + 1;
    let previous = Array.from({ length: cols }, (_, index) => index);
    for (let i = 1; i < rows; i += 1) {
        const current = [i, ...new Array(cols - 1).fill(0)];
        for (let j = 1; j < cols; j += 1) {
            const substitution = previous[j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1);
            const insertion = current[j - 1] + 1;
            const deletion = previous[j] + 1;
            current[j] = Math.min(substitution, insertion, deletion);
        }
        previous = current;
    }
    return previous[cols - 1];
}
export function draftRationale(input) {
    return {
        authority: "draft",
        findingId: input.finding.id,
        text: input.text,
        citations: input.evidence.map((row) => ({ evidenceRef: row.ref, quotedAt: row.observedAt })),
        draftedByWorker: input.worker,
        draftedAt: input.now,
    };
}
export function signRationale(input) {
    // Defended at runtime as well as in the type: a caller reaching this from
    // untyped JSON must not be able to present a "decision" as a draft.
    if (input.draft?.authority !== "draft")
        return { ok: false, reason: "draft-authority-invalid" };
    if (!hasText(input.actorId))
        return { ok: false, reason: "actor-required" };
    if (!hasText(input.editedText))
        return { ok: false, reason: "final-text-required" };
    return {
        ok: true,
        decision: {
            authority: "decision",
            findingId: input.draft.findingId,
            draftedText: input.draft.text,
            finalText: input.editedText,
            editDistance: editDistance(input.draft.text, input.editedText),
            citations: input.draft.citations,
            draftedByWorker: input.draft.draftedByWorker,
            actorId: input.actorId,
            signedAt: input.now,
        },
    };
}
/**
 * Share of drafted rationales signed with any edit at all. Reported so
 * rubber-stamping is visible in aggregate; a rate at or near zero across a
 * large population means the drafts are being signed unread.
 */
export function draftedRationaleEditRate(decisions) {
    const drafted = decisions.filter((row) => row.draftedByWorker != null);
    const edited = drafted.filter((row) => row.editDistance > 0);
    return {
        drafted: drafted.length,
        edited: edited.length,
        rate: drafted.length === 0 ? 0 : edited.length / drafted.length,
    };
}
